import { useState } from "react";
import { motion } from "framer-motion";
import { Dices, History } from "lucide-react";
import { toast } from "sonner";

const OUTCOME_LABEL = {
  erfolg: "ERFOLG",
  gemischt: "GEMISCHT",
  fehlschlag: "FEHLSCHLAG",
};

const OUTCOME_BADGE = {
  erfolg: "bg-hammerl-highlight text-hammerl-paper border-hammerl-ink",
  gemischt: "bg-hammerl-mixed text-hammerl-paper border-hammerl-ink",
  fehlschlag: "bg-hammerl-fail text-white border-hammerl-ink",
};

const RECENT_LIMIT = 10;

// Button-getriebene Spieleraktionen (Choice-/Preset-Klicks) gehören NICHT ins Sitzungsprotokoll:
// sie würden über Würfelergebnisse verraten, welche Option der GM als "gut/schlecht" bewertet.
// Heuristik: choice_type gesetzt (neuer +/-modifier-Klick) ODER source = 'choice'/'preset'.
// Frei getippte Aktionen (source='free_text' oder Legacy ohne source) bleiben sichtbar.
const isHiddenPlayerAction = (m) =>
  m.role === "player" && (m.choice_type != null || m.source === "choice" || m.source === "preset");

export default function StoryLog({ messages, onShareMessage, profile, onProfileChange }) {
  const [showAll, setShowAll] = useState(false);

  const filteredMessages = messages.filter((m) => !isHiddenPlayerAction(m));
  const hiddenCount = Math.max(0, filteredMessages.length - RECENT_LIMIT);
  const visibleMessages = showAll || hiddenCount === 0
    ? filteredMessages
    : filteredMessages.slice(-RECENT_LIMIT);

  return (
    <div className="max-w-3xl mx-auto bg-hammerl-card border-2 border-hammerl-ink shadow-brutal min-h-full relative paper-grain">
      <div className="border-b-2 border-hammerl-ink px-5 py-3 bg-hammerl-sidebar flex items-center justify-between">
        <div className="font-mono text-xs uppercase tracking-widest">Sitzungsprotokoll // Game Master Log</div>
        <div className="font-mono text-xs text-hammerl-pencil" data-testid="log-count">
          {visibleMessages.length} / {filteredMessages.length} Einträge
        </div>
      </div>
      <div className="notebook-margin px-6 sm:px-12 py-6 space-y-6" data-testid="story-log">
        {hiddenCount > 0 && (
          <div className="flex items-center justify-between border border-dashed border-hammerl-pencil bg-hammerl-sidebar px-4 py-2.5">
            <div className="font-mono text-[11px] uppercase tracking-widest text-hammerl-pencil flex items-center gap-2">
              <History className="w-3.5 h-3.5" />
              {showAll
                ? `Vollständiges Protokoll · ${filteredMessages.length} Einträge`
                : `${hiddenCount} ältere Einträge ausgeblendet`}
            </div>
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="font-mono text-[11px] uppercase tracking-widest underline decoration-hammerl-ink underline-offset-2 hover:text-hammerl-corpblue transition-colors"
              data-testid="toggle-history-btn"
            >
              {showAll ? "Nur letzte 10 zeigen" : "Komplette Historie laden"}
            </button>
          </div>
        )}
        {visibleMessages.map((m, idx) => (
          <motion.div
            key={m.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: Math.min(idx * 0.02, 0.3) }}
            data-testid={`message-${m.role}`}
            data-message-id={m.id}
          >
            {m.role === "gm" ? (
              <div className="group relative">
                <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil mb-1">
                  Game Master
                </div>
                <div className="font-handwriting text-xl sm:text-2xl text-hammerl-blue leading-relaxed whitespace-pre-wrap">
                  {m.content}
                </div>
              </div>
            ) : (
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil mb-1 flex items-center gap-2">
                  <span>Spieleraktion</span>
                  {m.dice_value !== null && m.dice_value !== undefined && (
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border-2 text-[10px] font-bold ${OUTCOME_BADGE[m.dice_outcome] || ""}`}>
                      <Dices className="w-3 h-3" />
                      d100: {m.dice_value} · {OUTCOME_LABEL[m.dice_outcome]}
                    </span>
                  )}
                </div>
                <div className="font-mono text-sm sm:text-base text-hammerl-ink leading-relaxed whitespace-pre-wrap pl-4 border-l-2 border-hammerl-ink">
                  {m.content}
                </div>
              </div>
            )}
          </motion.div>
        ))}
        {visibleMessages.length === 0 && (
          <div className="font-handwriting text-2xl text-hammerl-pencil italic text-center py-12">
            Stille im Stall... der Game Master ladet seine Galopp-Strategie.
          </div>
        )}
      </div>
    </div>
  );
}
